/**
 * asconShipment.ts — 경원(아스콘) 출하조회 도메인: 수주(OKIS R_SUJU)·계근(OKIS SELL).
 *
 * 근거: docs/references/경원-아스콘-OKIS-화면테이블.md §3·§4, wiki/업무/경원-시스템-준비.md 5차 ②(수주 = 출하조회, 10-22).
 *   수주 1건 = 하루 단위 주문(거래처·현장·규격·수주량 톤). 계근 1건 = 트럭 1대 = SELL 1행(S_DATE+S_OCODE 로 수주에 매달림).
 *   출하누계(V_OUTORDER) = Σ 계근 실중량(S_OUTSELL kg) ÷ 1000, 미출하(V_REORDER) = 수주량 − 출하누계 (712건 전부 일치 확인).
 *
 * 규칙(AGENTS §4-1·§4-8):
 *   - 경원 출하·영업은 OKIS 의 **읽기 전용 미러**. 우리 쪽에서 입력·수정하지 않는다(이 화면에 입력 탭 없음).
 *   - 단위는 **톤**. 출하량 정본 = 계근 실중량 `S_OUTSELL`(총중량 `S_ALLSELL` 아님).
 *   - 수주량은 중앙 출하 예정(yejung)과 같은 자리 — **실적으로 쓰지 않는다**.
 *   - 관급 = 거래처 코드 A002·A034 (domain/govTier.ts).
 *
 * 순수 타입·계산만. Firebase import 금지(domain 규칙).
 */
import { classifyTierFor, type GovTier } from "./govTier";
import { kstToday, type RqcShipment } from "./rqc";
import type { DailyShipment } from "./daily_shipments";

/** 수주 1건 (R_SUJU). 문서 키 = `${date}_${seq}` (수주 PK = ILJA+SEQ) */
export interface AsconOrder {
  id: string;
  /** 수주 일자 ILJA (YYYY-MM-DD) */
  date: string;
  /** 일자 내 순번 SEQ (2자리, 01~80 자동 · 81~ 수기 판매분과 짝) */
  seq: string;
  /** 거래처 코드 GRCD (4자리, 예 A002) — 관급 판정 키 */
  custCode: string;
  /** 현장 코드 CODE (3자리) */
  siteCode: string;
  /** 거래처명 V_INOUT (복사값) */
  company: string;
  /** 현장명 V_STATION (복사값) */
  site: string;
  /** 규격 코드 REMI_CODE (R_SIZE, 4자리) */
  sizeCode: string;
  /** 종류 KIND (표층용 WC-2, 기층용 BB-2 …) */
  kind: string;
  /** 골재 최대입자 V_RULE (13/20/30 mm) */
  maxAgg: string;
  /** 수주량 ORDER_M3 (톤 — 컬럼 이름만 레미콘 유래) */
  orderT: number;
  /** 출하누계 V_OUTORDER (톤) */
  shippedT: number;
  /** 미출하 V_REORDER (톤) */
  remainT: number;
  /** V_STATE 원문 — '대기중' | '마감' */
  state: string;
  /** 첫차시간 TIME_ (HH:MM) */
  firstTime: string;
  /** 거리 DIST (km) */
  distKm: number | null;
  /** 메모 MEMO */
  memo: string;
  /**
   * 위치 GROUND (라벨 "위 치") — 현장 위치 자유 글. 현장 테이블에는 주소 칸이 없어 이것이 유일한 위치 정보
   * (10-22 실데이터: 720건 중 134건, 읍·면·마을 수준). 수집기가 아직 안 옮기면 비어 있다.
   */
  location?: string;
}

/** 계근 1건 (SELL). 문서 키 = `${date}_${orderSeq}_${no}` */
export interface AsconTicket {
  id: string;
  /** 출하일자 S_DATE */
  date: string;
  /** 출하시간 S_TIME (HH:MM) */
  time: string;
  /** 수주코드 S_OCODE = R_SUJU.SEQ */
  orderSeq: string;
  /** 수주 안 출하 순번 S_CODE (1,2,3…) */
  no: number;
  /** 차량번호 S_CARNUM */
  carNo: string;
  /** 총중량 S_ALLSELL (kg) */
  grossKg: number;
  /** 공차중량 S_EMPSELL (kg) */
  tareKg: number;
  /** 납품량(실중량) S_OUTSELL (kg) — 출하량 정본 */
  netKg: number;
  /** 출하 온도 S_TEMPERATURE (℃) */
  tempC: number | null;
  /** 계근 담당 S_MAN */
  operator: string;
}

/**
 * 출하 진행 상태(화면 표시용). 출하량과 날짜로 정한다 — OKIS V_STATE 는 사실상 안 씀(대기중 719·마감 1, 10-22 실데이터).
 *   done    완료      — 수주량만큼 다 나감
 *   closed  출하마감  — 지난 날짜인데 덜 나감(날씨 등). 남은 양은 다른 날 수주로 옮겼을 수 있어 "남은 물량"으로 세지 않는다
 *   moving  출하 중   — 오늘·앞날, 일부 나감
 *   waiting 대기      — 오늘·앞날, 아직 안 나감. 수주량 0(미정)·여러 날에 나눠 잡은 수주도 대기(10-22 사용자 결정)
 */
export type AsconOrderStatus = "waiting" | "moving" | "done" | "closed";

export const ASCON_STATUS_LABEL: Record<AsconOrderStatus, string> = {
  waiting: "대기",
  moving: "출하 중",
  done: "완료",
  closed: "출하마감",
};

/** 톤 비교 여유 (소수 셋째 자리 반올림 오차) */
const EPS_T = 0.05;

/** today 기본 = KST 오늘. 수주 날짜가 그 전이면 지난 수주 */
export function asconOrderStatus(
  o: Pick<AsconOrder, "date" | "state" | "shippedT" | "remainT">,
  today: string = kstToday(),
): AsconOrderStatus {
  if (o.shippedT > 0 && o.remainT <= EPS_T) return "done";
  if (o.state === "마감" || o.date < today) return "closed";
  return o.shippedT > 0 ? "moving" : "waiting";
}

/** 더 나갈 수주인지 (대기·출하 중) — 완료·출하마감은 끝난 것 */
export function isOpenAsconOrder(st: AsconOrderStatus): boolean {
  return st === "waiting" || st === "moving";
}

export function asconTier(o: Pick<AsconOrder, "custCode">): GovTier {
  return classifyTierFor("gyeongwon", o.custCode);
}

/**
 * 앞으로 나갈 양(톤). 완료·출하마감 수주는 0 — 마지막 차 자투리(0.x톤)나 날씨로 못 나간 양(다른 날로 옮겼을 수 있음)을
 * "남은 물량"으로 세지 않는다. 더 나간 경우(음수)도 0. 실제 남은 양은 remainT 그대로 남아 있다(모달에 표시).
 */
export function openRemainT(o: Pick<AsconOrder, "date" | "state" | "shippedT" | "remainT">, today?: string): number {
  return isOpenAsconOrder(asconOrderStatus(o, today)) ? Math.max(0, o.remainT) : 0;
}

/** 계근 1건이 수기 보정인지 — 총중량 − 공차 ≠ 실중량 (OKIS 57건 확인, 공차·수기 계근 약점) */
export function isManualTicket(t: Pick<AsconTicket, "grossKg" | "tareKg" | "netKg">): boolean {
  return Math.abs(t.grossKg - t.tareKg - t.netKg) >= 1;
}

export const kgToT = (kg: number): number => kg / 1000;

export interface AsconSummary {
  orders: number;
  cntGov: number;
  cntCust: number;
  orderT: number;
  orderGovT: number;
  orderCustT: number;
  shippedT: number;
  remainT: number;
  /** 더 나갈 수주(대기·출하 중) 건수 — 완료·출하마감 제외 */
  remainOrders: number;
  /** 계근 건수 = 탕수 */
  trips: number;
  /** 운행한 차량 대수(중복 제거) */
  cars: number;
}

export function summarizeAscon(orders: AsconOrder[], tickets: AsconTicket[]): AsconSummary {
  const s: AsconSummary = {
    orders: orders.length, cntGov: 0, cntCust: 0,
    orderT: 0, orderGovT: 0, orderCustT: 0,
    shippedT: 0, remainT: 0, remainOrders: 0,
    trips: tickets.length,
    cars: new Set(tickets.map((t) => t.carNo)).size,
  };
  for (const o of orders) {
    s.orderT += o.orderT;
    s.shippedT += o.shippedT;
    s.remainT += openRemainT(o);
    if (isOpenAsconOrder(asconOrderStatus(o))) s.remainOrders += 1;
    if (asconTier(o) === "gov") { s.cntGov += 1; s.orderGovT += o.orderT; }
    else { s.cntCust += 1; s.orderCustT += o.orderT; }
  }
  return s;
}

/** 월별 출하(톤) — 출하누계(계근 실중량 합) 기준. 수주량(예정)은 쓰지 않는다 */
export interface AsconMonthBucket {
  /** YYYY-MM */
  ym: string;
  govT: number;
  custT: number;
  totalT: number;
}

/** 그 해 1~12월 칸을 만들고 수주의 출하누계를 관급·사급으로 더한다(다른 해 수주는 건너뜀) */
export function asconMonthBuckets(year: number, orders: Pick<AsconOrder, "date" | "custCode" | "shippedT">[]): AsconMonthBucket[] {
  const out = Array.from({ length: 12 }, (_, i) => ({
    ym: `${year}-${String(i + 1).padStart(2, "0")}`, govT: 0, custT: 0, totalT: 0,
  }));
  for (const o of orders) {
    if (!o.date.startsWith(`${year}-`)) continue;
    const b = out[Number(o.date.slice(5, 7)) - 1];
    if (!b) continue;
    if (asconTier(o) === "gov") b.govT += o.shippedT;
    else b.custT += o.shippedT;
    b.totalT += o.shippedT;
  }
  return out;
}

export interface AsconCarRow {
  carNo: string;
  trips: number;
  netT: number;
  first: string;
  last: string;
}

/** 차량별 운행 요약 (OKIS "차량별 운행보기"와 같은 자리) — 탕수 많은 순 */
export function summarizeAsconCars(tickets: AsconTicket[]): AsconCarRow[] {
  const by = new Map<string, AsconCarRow>();
  for (const t of tickets) {
    const r = by.get(t.carNo) ?? { carNo: t.carNo, trips: 0, netT: 0, first: t.time, last: t.time };
    r.trips += 1;
    r.netT += kgToT(t.netKg);
    if (t.time < r.first) r.first = t.time;
    if (t.time > r.last) r.last = t.time;
    by.set(t.carNo, r);
  }
  return [...by.values()].sort((a, b) => b.trips - a.trips || b.netT - a.netT || a.carNo.localeCompare(b.carNo));
}

/** 위치 + 메모를 중앙 memo1 꼴("/" 구분) 한 줄로 */
export function asconMemoText(o: Pick<AsconOrder, "memo" | "location">): string {
  return [o.location ?? "", o.memo].map((s) => s.trim()).filter(Boolean).join(" / ");
}

/** 메모 표시 줄 — 중앙 송장 모달 splitMemo 와 같은 규칙("/"·"|" 로 나눔) + 줄바꿈 */
export function asconMemoLines(o: Pick<AsconOrder, "memo" | "location">): string[] {
  return asconMemoText(o).split(/[/|\r\n]+/).map((s) => s.trim()).filter(Boolean);
}

/** 계근 중 가장 이른 시각 (HH:MM), 없으면 "" */
export function firstTicketTime(tickets: Pick<AsconTicket, "time">[]): string {
  return tickets.reduce((a, t) => (t.time && (!a || t.time < a) ? t.time : a), "");
}

/** 계근 평균 출하 온도(℃, 소수 첫째 자리). 온도 기록이 없으면 null */
export function avgTempC(tickets: Pick<AsconTicket, "tempC">[]): number | null {
  const ts = tickets.map((t) => t.tempC).filter((v): v is number => v != null && Number.isFinite(v));
  return ts.length ? Math.round((ts.reduce((a, v) => a + v, 0) / ts.length) * 10) / 10 : null;
}

/** 입도 계열 — WC(표층용)·BB(기층용). 그 밖(MC·SMA·칼라 등)은 null */
export type AsconGradeFamily = "wc" | "bb";

export interface AsconSpec {
  /** 규격 이름 — "표층용(WC-2)1등급", 등급 없으면 "표층용(WC-2)" */
  name: string;
  /** 입도 기호 — "WC-2" (없으면 "") */
  grade: string;
  gradeFamily: AsconGradeFamily | null;
  /** 골재치수 — "13mm" */
  agg: string;
}

/**
 * 규격 정리 — KIND + V_RULE → 이름 "표층용(WC-2)1등급" · 골재치수 "13mm" (10-22 사용자 지정, 층 칸 없앰).
 * 실데이터(R_SUJU 720건) 꼴이 제각각이라 맞춘다:
 *   "표층용 (WC-4)"·"표층용 WC-1"·"중간층용 MC-1" → 괄호 꼴 / "(WC-6) 1등급"·"(WC-2)-1등급" → "(WC-6)1등급"
 *   V_RULE "13"·"13mm" → "13mm" / KIND·V_RULE 가 서로 바뀐 2건("13mm" | "표층용(WC-2)-1등급") 은 되돌림
 *   "SMA10mm상부"·"칼라아스콘(암갈색)" 같은 것은 그대로
 */
export function asconSpec(o: Pick<AsconOrder, "kind" | "maxAgg">): AsconSpec {
  let kind = o.kind.trim();
  let rule = o.maxAgg.trim();
  const isAgg = (v: string) => /^\d+(\.\d+)?\s*(mm)?$/i.test(v);
  if (isAgg(kind) && rule && !isAgg(rule)) [kind, rule] = [rule, kind];
  const name = kind
    .replace(/\s*\(\s*/g, "(")
    .replace(/\s*\)/g, ")")
    .replace(/^(\S+용)\s+([A-Z]{2}(?:-\d+)?)(?=$|\s|\d)/, "$1($2)")
    .replace(/\)\s*-?\s*(\d+)\s*등급/, ")$1등급")
    .trim();
  const grade = name.match(/\(([A-Z]{2}(?:-\d+)?)\)/)?.[1] ?? "";
  const fam = grade.slice(0, 2);
  const m = rule.match(/^(\d+(?:\.\d+)?)\s*(mm)?$/i);
  return {
    name,
    grade,
    gradeFamily: fam === "WC" ? "wc" : fam === "BB" ? "bb" : null,
    agg: m ? `${m[1]}mm` : "",
  };
}

/** 규격 한 줄 글자 (정렬·검색·칩) — "표층용(WC-2)1등급 · 13mm" */
export function asconSpecLabel(o: Pick<AsconOrder, "kind" | "maxAgg">): string {
  const s = asconSpec(o);
  return [s.name, s.agg].filter(Boolean).join(" · ");
}

/** 수주별 계근 (날짜·수주코드 일치), 시각순 */
export function ticketsOfOrder(o: Pick<AsconOrder, "date" | "seq">, tickets: AsconTicket[]): AsconTicket[] {
  return tickets.filter((t) => t.date === o.date && t.orderSeq === o.seq).sort((a, b) => a.no - b.no);
}

/**
 * 중앙 출하조회 부품(금일 카드·스케쥴 시트·타임라인·MO 카드)이 받는 RqcShipment 모양으로 옮긴다 — 표시 전용.
 * tier 를 직접 넣고 scode 는 비운다(중앙 C023 판정을 타지 않게).
 */
export function asconOrdersToCardRows(orders: AsconOrder[], tickets: AsconTicket[]): RqcShipment[] {
  return orders.map((o, i) => {
    const ts = ticketsOfOrder(o, tickets);
    const last = ts.length ? ts[ts.length - 1].time.replace(":", "") : "";
    return {
      id: o.id, date: o.date, company: o.company, site: o.site,
      kind: o.kind, code: o.sizeCode,
      // 규격 = "표층용(WC-2)1등급 · 13mm" (정렬·검색). 화면 칩은 AsconSpecChip
      mix: asconSpecLabel(o),
      // 위치 + 메모 = 중앙 RQC 메모(memo1) 자리 — "/" 로 이어 중앙과 같은 방식으로 줄을 나눠 보여 준다(10-22 사용자 지정)
      memo1: asconMemoText(o),
      qty: o.orderT, shipped: o.shippedT, remain: openRemainT(o),
      cumcars: ts.length,
      // 첫차시간(TIME_)은 거의 입력 안 함("00:00" → 빈칸) — 비면 그날 첫 계근 시각으로(10-22 사용자 지정)
      firstcar: (o.firstTime || firstTicketTime(ts)).replace(":", ""),
      lastcar: last ? `(${last})` : "",
      tier: asconTier(o),
      // 타임라인 계근 시각 키(그날 안에서만 유일). 중앙 sc(현장 코드)와 섞이지 않게 metaOverride 로만 쓴다
      sc: i + 1,
    };
  });
}

// ── 출하실적(/shipment-stats) — 중앙 화면 부품이 받는 모양으로 옮긴다(표시 전용) ─────────────────

/**
 * 날짜별 출하 → 중앙 출하실적의 일 집계(DailyShipment) 모양. 단위는 **톤**(칸 이름만 *_m3).
 *   실적 gov_m3/cust_m3 = 그날 수주 출하누계(계근 실중량 합)를 관급·사급으로 — 출하조회 월별 카드(asconMonthBuckets)와 같은 기준
 *   예정 자리 *_planned_m3 = 수주량(중앙 예정과 같은 자리 — 실적으로 쓰지 않는다, AGENTS §4-8)
 *   trips = 그날 계근 건수(탕수)
 */
export function asconDailyShipments(
  orders: Pick<AsconOrder, "date" | "custCode" | "orderT" | "shippedT">[],
  tickets: Pick<AsconTicket, "date">[],
): Map<string, DailyShipment> {
  const out = new Map<string, DailyShipment>();
  const get = (date: string): DailyShipment => {
    let d = out.get(date);
    if (!d) {
      d = {
        id: date, date, gov_m3: 0, cust_m3: 0, total_m3: 0,
        gov_planned_m3: 0, cust_planned_m3: 0, total_planned_m3: 0, trips: 0, source: "okis",
      };
      out.set(date, d);
    }
    return d;
  };
  for (const o of orders) {
    if (!o.date) continue;
    const d = get(o.date);
    if (asconTier(o) === "gov") { d.gov_m3 += o.shippedT; d.gov_planned_m3! += o.orderT; }
    else { d.cust_m3 += o.shippedT; d.cust_planned_m3! += o.orderT; }
    d.total_m3 += o.shippedT;
    d.total_planned_m3! += o.orderT;
  }
  for (const t of tickets) if (t.date) get(t.date).trips! += 1;
  return out;
}
